<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :title="$t('resources.favorites_blocks.title')"
    :sub-title="$t('resources.favorites_blocks.subtitle')">
    <template #actions>
      <v-btn
        v-if="!listPage"
        outlined
        color="primary"
        class="left-icon"
        :to="{ name: 'favorites_blocks_create', query: { domainId } }">
        <plus-icon class="mr-2 fill--primary"></plus-icon>
        {{ $t("generic.buttons.create") }}
      </v-btn>
      <v-btn
        v-else-if="totalItems || noData || loading"
        color="primary"
        class="left-icon"
        to="/favorites-blocks/create">
        <plus-icon class="mr-2"></plus-icon>
        {{ $t("generic.buttons.create") }}
      </v-btn>
    </template>
    <template #default>
      <delete-resource-modal :resource-name="resourceName"></delete-resource-modal>
      <va-list
        :resource="resourceName"
        disable-query-string
        :filter.sync="customFiltersValues"
        :filters="filters"
        :domains-list="domainsList"
        disable-export
        disable-create
        :ignore-domain-filter="!!domainId"
        :hide-domain-filter="!listPage"
        :items-per-page="10"
        @update:selected="getBulkSelectedItems"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <empty-list
            :list-page="listPage"
            :title="$t('resources.favorites_blocks.empty_list_title')"
            :height="listPage ? '450px' : '300px'"
            :light-image="emptyLightImg"
            :dark-image="emptyDarkImg"
            :route="
              listPage ? { name: 'favorites_blocks_create' } : { name: 'favorites_blocks_create', query: { domainId } }
            ">
            <template #subtitle>
              {{ $t("resources.favorites_blocks.empty_list_subtitle_1") }}
              <a
                href="https://help.siterunner.com/help/favorites-block-how-to-create-a-favorites-block "
                target="_blank"
                class="help-link"
                >{{ $t("resources.favorites_blocks.empty_list_subtitle_link") }}</a
              >{{ $t("resources.favorites_blocks.empty_list_subtitle_2") }}
            </template>
          </empty-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template v-slot:[`bulk.actions`]>
          <v-btn
            @click="editResource('favorites_blocks', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-pencil-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
          </v-btn>
          <v-btn
            @click="duplicateResource('favorites_blocks', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-file-multiple-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.duplicate") }}</span>
          </v-btn>
        </template>
        <template #[`filter.search`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table :fields="fields" class="va-table--selectable" @row-click="handleRowClick">
          <template #[`field.name`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <span>
                {{ item.name }}
              </span>
            </div>
          </template>
          <template #[`field.domain.name`]="{ item }">
            <div class="cell-ellipsis">
              <span>{{ item.domain.name }}</span>
            </div>
          </template>
          <template #[`field.createdAt`]="{ item }">
            {{ changeDateFormat(item.createdAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                {
                  title: 'Edit',
                  icon: 'mdi-pencil-outline',
                  function: editResource.bind(this, 'favorites_blocks', item.id),
                },
                {
                  title: 'Duplicate',
                  icon: 'mdi-file-multiple-outline',
                  function: duplicateResource.bind(this, 'favorites_blocks', item.id),
                },
                {
                  title: 'Delete',
                  icon: 'mdi-trash-can-outline',
                  function: deleteResource.bind(this, item),
                },
              ]">
            </DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import DataListActions from "@/components/custom/DataListActions";
  import PlusIcon from "@/components/icons/PlusIcon";
  import EmptyList from "@/components/custom/EmptyList";
  import debounce from "lodash.debounce";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import RowClick from "@/mixins/rowClickMixin";
  import emptyLightImg from "@/assets/images/empty-list/favorites-list-empty-list-light.svg";
  import emptyDarkImg from "@/assets/images/empty-list/favorites-list-empty-list-dark.svg";
  import DeleteResourceModal from "@/components/resources/DeleteResourceModal";

  export default {
    components: {
      ResourceListWrapper,
      DataListActions,
      PlusIcon,
      EmptyList,
      DeleteResourceModal,
    },
    mixins: [ConnectedDomainsList, RowClick],
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      activeFilterOnload: {
        type: Object,
        default: () => ({}),
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
      domainId: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        changeDateFormat,
        isUserUS,
        noData: false,
        loading: true,
        totalItems: 0,
        emptyLightImg,
        emptyDarkImg,
        customFiltersValues: this.activeFilterOnload,
        filters: [
          {
            source: "search",
            alwaysOn: true,
          },
        ],
        fields: [
          {
            source: "name",
            sortable: true,
            label: this.$t("resources.favorites_blocks.fields.name"),
            width: "50%",
          },
          {
            source: "domain.name",
            label: this.$t("resources.favorites_blocks.fields.domain"),
            sortable: true,
            width: "50%",
          },
          {
            source: "createdAt",
            sortable: true,
            label: this.$t("resources.favorites_blocks.fields.created"),
            width: "184px",
          },
          {
            source: "updatedAt",
            sortable: true,
            label: this.$t("resources.favorites_blocks.fields.last_updated"),
            width: "184px",
          },
        ],
        bulkSelectedItems: [],
      };
    },
    created() {
      if (this.listPage) {
        this.filters.push({
          source: "domain",
          type: "select",
          attributes: {
            reference: "domains",
            itemText: "name",
          },
        });
      }
    },
    methods: {
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      getBulkSelectedItems(val) {
        this.bulkSelectedItems = val;
      },
      async deleteResource(item) {
        this.$store.dispatch(`${this.resourceName}/displayDeleteModal`, {
          displayDeleteModal: true,
          items: [item],
        });
      },
      duplicateResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_create`, query: { source: resourceId } });
      },
      editResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_edit`, params: { id: resourceId }, query: { domainId: this.domainId } });
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
    },
  };
</script>
<style scoped lang="scss"></style>
